import { Box, Modal } from '@mui/material';
import * as loadingAnimationData from 'core/assets/lottie/green-loader-animation.json';
import { ReactComponent as Cross } from 'core/assets/svg/common/cross.svg';
import { ReactComponent as Arrow } from 'core/assets/svg/modal-icons/arrow-left.svg';
import { ReactComponent as Info } from 'core/assets/svg/studio-tab/feed-prompts-modal/info.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';
import { FeedViews } from 'core/constants/enums/FeedViews.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import LottieAnimation from 'core/layouts/components/lottie-animation/LottieAnimation';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { HISTORY } from 'core/navigation/Routes';
import {
	feedPromptModalDetailsSelector,
	feedPromptModalHasMultipleImagesSelector,
	feedPromptsModalTypeSelector,
	openFeedPromptsModalSelector,
	publishPromptAsync,
	showConfirmPromptScreenSelector,
	toggleFeedPromptsModal,
} from 'core/store/reducers/appReducer/AppSlice';
import { switchFeedPromptsModalSelectedImage } from 'core/store/reducers/appReducer/appSliceThunk';
import {
	openDeletePromptConfirmationModalSelector,
	studioFeedViewSelector,
} from 'core/store/reducers/studioReducer/StudioSlice';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModelDetails from './feed-details/ModelDetails';
import StyleDetails from './feed-details/StyleDetails';
import { feedPromptsModalStyles } from './FeedPromptsModalStyles';
import FeedPromptsModalTextFieldInput from './input-comp/InputComponents';

function FeedPromptsModal() {
	const dispatch = useDispatch();

	const isModalOpen = useSelector(openFeedPromptsModalSelector);
	const feedPromptsModalType = useSelector(feedPromptsModalTypeSelector);
	const selectedPrompt = useSelector(feedPromptModalDetailsSelector);
	const multipleImages = useSelector(feedPromptModalHasMultipleImagesSelector);
	const open = isModalOpen && feedPromptsModalType === HISTORY;
	const feedViewType = useSelector(studioFeedViewSelector);
	const isNotGridView = feedPromptsModalType === HISTORY || feedViewType !== FeedViews.GRID;
	const openDeletePromptConfirmationModal = useSelector(openDeletePromptConfirmationModalSelector);
	const showConfirmPromptScreen = useSelector(showConfirmPromptScreenSelector);

	const [nameInput, setNameInput] = useState('');
	const [keywordsInput, setKeywordsInput] = useState('');
	const [hidePrompt, setHidePrompt] = useState(true);
	const [hideNegativePrompt, setHideNegativePrompt] = useState(true);

	const handleClose = () => {
		dispatch(toggleFeedPromptsModal({ openModal: false }));
	};

	useEffect(() => {
		if (showConfirmPromptScreen) {
			setNameInput('');
			setKeywordsInput('');
		}
	}, [showConfirmPromptScreen]);

	const resetShowMoreStates = () => {
		setHidePrompt(true);
		setHideNegativePrompt(true);

		const element = document.getElementById(ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR);
		if (element) {
			element.scrollIntoView({ block: 'end', behavior: 'smooth' });
		}
	};

	const enableArrowButtons = useMemo(
		() =>
			multipleImages &&
			!showConfirmPromptScreen &&
			!openDeletePromptConfirmationModal &&
			isNotGridView &&
			feedPromptsModalType === HISTORY,
		[
			multipleImages,
			showConfirmPromptScreen,
			openDeletePromptConfirmationModal,
			isNotGridView,
			feedPromptsModalType,
		],
	);

	const leftArrowClick = useCallback(
		(e) => {
			e.stopPropagation();
			if (enableArrowButtons) {
				dispatch(switchFeedPromptsModalSelectedImage(-1));
				resetShowMoreStates();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[dispatch, enableArrowButtons],
	);
	const rightArrowClick = useCallback(
		(e) => {
			e.stopPropagation();
			if (enableArrowButtons) {
				dispatch(switchFeedPromptsModalSelectedImage());
				resetShowMoreStates();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[dispatch, enableArrowButtons],
	);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="prompt-info-modal"
			aria-describedby="prompt details"
			sx={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2.5px)' }}
		>
			<Box id="modal-wrapper" sx={feedPromptsModalStyles.modalWrapper} onClick={handleClose}>
				<Box id="modal-body" sx={feedPromptsModalStyles.modalBody}>
					<BaseButton
						id="cross-button"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						styles={[feedPromptsModalStyles.crossButton]}
					>
						<Cross id="cross-icon" />
					</BaseButton>

					{enableArrowButtons && (
						<Box
							id="arrow-left"
							sx={[reusableStyles.desktopOnly, feedPromptsModalStyles.arrowButton]}
						>
							<Arrow id="arrow-left" onClick={leftArrowClick} />
						</Box>
					)}

					<Box
						id="content-wrapper"
						sx={[feedPromptsModalStyles.contentWrapper]}
						onClick={(e) => e.stopPropagation()}
					>
						{!showConfirmPromptScreen && (
							<Box id="detail-screen-content" sx={[feedPromptsModalStyles.detailScreenContent]}>
								<Box id="image-wrapper" sx={feedPromptsModalStyles.detailScreenImgWrapper}>
									<Box id="image-cont" sx={feedPromptsModalStyles.detailScreenImgCont}>
										<Box id="image" sx={feedPromptsModalStyles.image}>
											{selectedPrompt.loading ? (
												<Box sx={{ height: '90px', width: '180px' }}>
													<LottieAnimation animationData={loadingAnimationData} loop />
												</Box>
											) : (
												<BaseImage src={selectedPrompt?.thumbnail} alt="" />
											)}
										</Box>
									</Box>
								</Box>
								{selectedPrompt.styleId ? (
									<StyleDetails
										selectedPrompt={selectedPrompt}
										handleClose={handleClose}
										feedPromptsModalType={feedPromptsModalType}
										hidePrompt={hidePrompt}
										setHidePrompt={setHidePrompt}
										hideNegativePrompt={hideNegativePrompt}
										setHideNegativePrompt={setHideNegativePrompt}
									/>
								) : (
									<ModelDetails
										selectedPrompt={selectedPrompt}
										handleClose={handleClose}
										feedPromptsModalType={feedPromptsModalType}
										hidePrompt={hidePrompt}
										setHidePrompt={setHidePrompt}
										hideNegativePrompt={hideNegativePrompt}
										setHideNegativePrompt={setHideNegativePrompt}
									/>
								)}
							</Box>
						)}
						{showConfirmPromptScreen && (
							<Box
								id="confirm-screen-content-wrapper"
								sx={[feedPromptsModalStyles.confirmScreenWrapper]}
							>
								<Box sx={{ padding: '16px 24px' }}>
									<BaseTypography variant="body1" styles={{ color: 'colors.tonal1' }}>
										Prompt details
									</BaseTypography>
								</Box>
								<Box id="confirm-screen-content" sx={[feedPromptsModalStyles.confirmScreenContent]}>
									<Box id="details-cont" sx={feedPromptsModalStyles.confirmScreenPromptDetailsCont}>
										<Box
											id="details-scroll-wrapper"
											sx={[
												feedPromptsModalStyles.promptDetailsScrollWrapper,
												reusableStyles.verticalScrollBarStyles,
												{
													'&::-webkit-scrollbar-track': { backgroundColor: 'colors.dark6' },
													'&::-webkit-scrollbar-thumb': {
														border: `4px solid ${colorPalette.dark6}`,
													},
												},
											]}
										>
											<Box
												id="prompt-details"
												sx={feedPromptsModalStyles.confirmScreenPromptDetails}
											>
												<Box
													id="prompt-section"
													sx={[reusableStyles.flexCol, { alignItems: 'start', gap: '4px' }]}
												>
													<BaseTypography
														variant="b2_caption_medium"
														styles={{ color: 'colors.light3' }}
													>
														Prompt
													</BaseTypography>
													<BaseTypography variant="b2_caption" styles={{ color: 'colors.light1' }}>
														{selectedPrompt.prompt}
													</BaseTypography>
												</Box>

												<Box
													id="model-section"
													sx={[
														reusableStyles.flexRow,
														{
															justifyContent: 'space-between',
															gap: '4px',
														},
													]}
												>
													<BaseTypography
														variant="b2_caption_medium"
														styles={{ color: 'colors.light3' }}
													>
														Model
													</BaseTypography>
													<Box sx={feedPromptsModalStyles.modelDetail}>
														<Box sx={feedPromptsModalStyles.modelImage}>
															<BaseImage src={selectedPrompt.modelImage} alt="" />
														</Box>
														<BaseTypography
															variant="b2_caption"
															styles={{ color: 'colors.light1' }}
														>
															{selectedPrompt.modelLabel}
														</BaseTypography>
													</Box>
												</Box>
												<Box id="prompt-details-divider" sx={feedPromptsModalStyles.divider} />

												<Box id="auto-assign-section" sx={[reusableStyles.flexRow, { gap: '8px' }]}>
													<Info />
													<BaseTypography
														variant="b2_caption_medium"
														styles={{ color: 'colors.light4' }}
													>
														Our AI will auto assign it if you leave it blank
													</BaseTypography>
												</Box>

												<FeedPromptsModalTextFieldInput
													id="name"
													title="Name"
													subtitle="Suggest a title for this prompt"
													placeholder="Example: A cute dog"
													value={nameInput}
													onChangeFunc={(e) => setNameInput(e.target.value)}
												/>

												<FeedPromptsModalTextFieldInput
													id="name"
													title="Keywords"
													subtitle="Enter at least five words related to your image separated by comma"
													placeholder="Example: anime, photoreal, animal ... "
													value={keywordsInput}
													onChangeFunc={(e) => setKeywordsInput(e.target.value)}
												/>
											</Box>
										</Box>
										<Box
											id="confirm-cont-wrapper"
											sx={feedPromptsModalStyles.confirmScreenButtonsCont}
										>
											<BaseButton
												id="confirm-publish-prompt"
												styles={[
													feedPromptsModalStyles.buttonStyles,
													feedPromptsModalStyles.publishPromptButton,
												]}
												onClick={(e) => {
													e.stopPropagation();
													dispatch(
														publishPromptAsync({
															nameInput,
															keywordsInput,
														}),
													);
												}}
											>
												<BaseTypography variant="b1_bold" styles={{ color: 'colors.dark6' }}>
													Confirm
												</BaseTypography>
											</BaseButton>
										</Box>
									</Box>

									<Box id="image-wrapper" sx={feedPromptsModalStyles.confirmScreenImgWrapper}>
										<Box id="image-cont" sx={feedPromptsModalStyles.confirmScreenImgCont}>
											<Box id="image" sx={feedPromptsModalStyles.image}>
												<BaseImage src={selectedPrompt?.image} alt="" />
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
						)}
					</Box>

					{enableArrowButtons && (
						<Box
							id="arrow-right"
							sx={[reusableStyles.desktopOnly, feedPromptsModalStyles.arrowButton]}
						>
							<Arrow
								id="arrow-right"
								style={{ transform: 'rotate(180deg)' }}
								onClick={rightArrowClick}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</Modal>
	);
}

export default FeedPromptsModal;
